import {
  Box,
  Button,
  Stack,
  StackDivider,
  VStack,
  Text,
  HStack,
  DrawerBody,
  DrawerHeader,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { BrightnessFilterMenuItem } from '$/components/core/Collection/CollectionFilter/components/BrightnessFilter/BrightnessFilterMenuItem';
import { MobileFilter } from '$/components/core/Collection/CollectionFilter/components/MobileFilter/MobileFilter';
import { useFilterOptions } from '$/hooks/data/useFilterOptions';
import { useCollectionViewOptions } from '$/hooks/useCollectionViewOptions';
import { useSwipeHook } from '$/hooks/useSwipeHook';
import { TooltipLabel } from '$/pages/DashboardPages/components/TooltipLabel';
import {
  collectionStore,
  getCollectionStore,
  useCollectionStore,
} from '$/stores/useCollectionStore';

interface Props {
  closeFilterDrawer: () => void;
  setSubFilter: (filterSearchName: string) => void;
  amount?: number;
}

export const MobileMainFilterPage: FC<Props> = ({
  closeFilterDrawer,
  setSubFilter,
  amount,
}) => {
  const { t } = useTranslation();

  const { data: possibleFilter } = useFilterOptions();
  const brightnessFilter = useCollectionStore.useBrightnessFilter();
  const activeType = useCollectionStore.useActiveType();
  const selectedViewMode = useCollectionStore.useSelectedViewMode();

  const viewModeOptions = useCollectionViewOptions();

  const swipeEvents = useSwipeHook({
    onSwipeVertical: (direction) => direction === 'down' && closeFilterDrawer(),
  });

  const filters = [
    ...(possibleFilter?.general ?? []),
    ...(possibleFilter?.additional ?? []),
  ].filter((filter) => filter.searchField !== 'collection');

  return (
    <Box>
      <DrawerHeader {...swipeEvents}>
        <HStack alignItems='baseline' justify='space-between'>
          <Text fontSize='md'>
            {t('dashboard.collection.filter.mobile.filterHeader')}
          </Text>
          <Button fontSize='sm' onClick={closeFilterDrawer} variant='text'>
            {t('dashboard.collection.filter.mobile.cancelButton')}
          </Button>
        </HStack>
      </DrawerHeader>

      <DrawerBody>
        <VStack
          align='flex-start'
          divider={<StackDivider mt='1' color='mobileStackDivider' />}
          spacing={4}
        >
          {viewModeOptions.length > 0 && (
            <Box w='full'>
              <Text mb='3' color='lighterText' fontSize='sm' fontWeight='bold'>
                {t('dashboard.collection.displayOptions')}
              </Text>
              <HStack gap='2'>
                {viewModeOptions.map((option) => {
                  const isActive = selectedViewMode === option.name;
                  return (
                    <IconButton
                      key={option.name}
                      color={
                        isActive ? 'primaryButton.activeBackground' : 'text'
                      }
                      bg={
                        isActive
                          ? 'primaryButton.lightBackground'
                          : 'transparent'
                      }
                      border='1px solid'
                      borderColor={isActive ? 'form.activeBorder' : 'border'}
                      borderRadius='4px'
                      aria-label=''
                      icon={<Icon icon={option.name} height='16px' />}
                      onClick={() =>
                        collectionStore.setState({
                          selectedViewMode: option.name,
                        })
                      }
                      size='sm'
                      variant='text'
                    />
                  );
                })}
              </HStack>
            </Box>
          )}

          {filters.map((filter) => (
            <Box key={filter.searchField} w='full'>
              <Text color='lighterText' fontSize='sm' fontWeight='bold'>
                {filter.displayName}
              </Text>
              <MobileFilter
                filterGroup={filter}
                onSingleFilterSelected={setSubFilter}
              />
            </Box>
          ))}

          {activeType === 'color_tint' && (
            <Box w='full'>
              <HStack justify='space-between'>
                <Text color='lighterText' fontWeight='bold'>
                  {t('dashboard.collection.filter.brightness')}
                </Text>

                <Tooltip
                  border='1px'
                  borderColor='neutral.50'
                  shadow='base'
                  hasArrow
                  label={t('inspirationEditor.filter.resetBrightness')}
                  openDelay={500}
                >
                  <span>
                    <IconButton
                      minW={0}
                      aria-label={t('inspirationEditor.filter.resetBrightness')}
                      icon={<Icon icon='refresh' w='10px' />}
                      isDisabled={
                        brightnessFilter[0] === 0 && brightnessFilter[1] === 100
                      }
                      onClick={() =>
                        useCollectionStore
                          .getState()
                          .setBrightnessFilter([0, 100])
                      }
                      variant='text'
                    />
                  </span>
                </Tooltip>
              </HStack>

              <BrightnessFilterMenuItem p='0' />

              <Stack gap='4' p='3'>
                <TooltipLabel
                  label={t('dashboard.collection.whatIsBrightness')}
                  info={t('dashboard.collection.brightnessExplanation')}
                />
              </Stack>
            </Box>
          )}

          <HStack justify='space-between' gap='4' w='100%'>
            <Button
              h='32px'
              fontSize='sm'
              onClick={getCollectionStore().resetAppliedFilter}
              variant='text'
            >
              {t('dashboard.collection.resetFilter')}
            </Button>

            <Button px='6' py='3' fontSize='sm' onClick={closeFilterDrawer}>
              {amount
                ? t('dashboard.collection.resultsAmount.showAmount', { amount })
                : t('dashboard.collection.resultsAmount.showGeneral')}
            </Button>
          </HStack>
        </VStack>
      </DrawerBody>
    </Box>
  );
};
